import { __assign, __rest } from "tslib";
import { useReducer, useEffect, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
// defines the time for the animation between slides in milliseconds
var transitionTime = 400;
// defines the threshold when to accept a swipe
var threshold = 0.3;
// defines the limit for swiping (max. the next full and a bit)
var limit = 1.2;
// animation to be used when bouncing back
var elastic = "transform ".concat(transitionTime, "ms cubic-bezier(0.68, -0.55, 0.265, 1.55)");
// animation to be used when automatically sliding
var smooth = "transform ".concat(transitionTime, "ms ease");
var initialCarouselState = {
    desired: 0,
    active: 0,
};
function previous(length, current) {
    return (current - 1 + length) % length;
}
function next(length, current) {
    return (current + 1) % length;
}
function makeLoop(cb, interval) {
    var id;
    var loop = {
        start: function () {
            id = setTimeout(cb, interval);
        },
        reset: function () {
            loop.stop();
            loop.start();
        },
        stop: function () {
            clearTimeout(id);
        },
    };
    return loop;
}
function carouselReducer(state, action) {
    switch (action.type) {
        case 'jump':
            return __assign(__assign({}, state), { desired: action.desired });
        case 'next':
            return __assign(__assign({}, state), { desired: next(action.length, state.active) });
        case 'prev':
            return __assign(__assign({}, state), { desired: previous(action.length, state.active) });
        case 'done':
            return __assign(__assign({}, state), { active: state.desired });
        default:
            return state;
    }
}
function swiped(delta, dispatch, length, threshold, dir, setOffset) {
    var distance = dir * delta;
    if (distance >= threshold) {
        dispatch(dir > 0 ? { type: 'prev', length: length } : { type: 'next', length: length });
    }
    else {
        setOffset(0);
    }
}
function getShift(o, active, desired, total) {
    if (!o) {
        var end = total - 3;
        if (!desired && active === end) {
            o = -1;
        }
        else if (desired === end && !active) {
            o = 1;
        }
    }
    if (o) {
        var pref = Math.sign(o);
        return (100 * pref) / total;
    }
    else {
        var diff = active - desired;
        return (100 * diff) / total;
    }
}
export function useCarousel(length, interval) {
    var _a = useReducer(carouselReducer, initialCarouselState), state = _a[0], dispatch = _a[1];
    var loop = useRef(undefined);
    var container = useRef(undefined);
    var offset = useRef(0);
    var updateOffset = function () {
        var c = container.current;
        if (c) {
            var o = offset.current;
            var transform = 'translateX(0)';
            var transition = 'none';
            if (state.desired !== state.active) {
                var shift = getShift(o, state.active, state.desired, length + 2);
                transition = smooth;
                transform = "translateX(".concat(shift, "%)");
            }
            else if (!isNaN(o)) {
                if (o !== 0) {
                    transform = "translateX(".concat(o, "px)");
                }
                else {
                    transition = elastic;
                }
            }
            c.style.transform = transform;
            c.style.transition = transition;
        }
    };
    var adjustOffset = function (value) {
        var _a;
        offset.current = value;
        (_a = loop.current) === null || _a === void 0 ? void 0 : _a.reset();
        updateOffset();
    };
    var _b = useSwipeable({
        onSwiping: function (e) {
            var sign = Math.sign(e.deltaX);
            var c = container.current.firstElementChild;
            adjustOffset(sign * Math.min(Math.abs(e.deltaX), limit * c.clientWidth));
        },
        onSwipedLeft: function (e) {
            var c = container.current.firstElementChild;
            var t = c.clientWidth * threshold;
            swiped(e.deltaX, dispatch, length, t, -1, adjustOffset);
        },
        onSwipedRight: function (e) {
            var c = container.current.firstElementChild;
            var t = c.clientWidth * threshold;
            swiped(e.deltaX, dispatch, length, t, +1, adjustOffset);
        },
        trackMouse: true,
        trackTouch: true,
    }), ref = _b.ref, otherHandlers = __rest(_b, ["ref"]);
    var handlers = __assign(__assign({}, otherHandlers), { ref: function (value) {
            container.current = value;
            updateOffset();
            return ref(value);
        }, style: {
            width: "".concat(100 * (length + 2), "%"),
            left: "-".concat((state.active + 1) * 100, "%"),
        } });
    if (!loop.current) {
        loop.current = makeLoop(function () { return dispatch({ type: 'next', length: length }); }, interval);
    }
    useEffect(function () {
        loop.current.start();
        return loop.current.stop;
    }, [state.active]);
    useEffect(function () {
        var id = setTimeout(function () {
            offset.current = NaN;
            dispatch({ type: 'done' });
        }, transitionTime);
        return function () { return clearTimeout(id); };
    }, [state.desired]);
    updateOffset();
    return [state.active, function (n) { return dispatch({ type: 'jump', desired: n }); }, handlers];
}
//# sourceMappingURL=useCarousel.js.map